import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { featuresQuery, isHardwareEnabledInMap } from "@/api/queries";
import type { HardwareId } from "@/contracts";
import { apiClient } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import { MarketingChrome } from "./marketing-chrome";
import { useMarketingPageMeta } from "./use-marketing-page-meta";

interface EquipmentCard {
  id: string;
  /** Joins this card to FEATURE_FLAGS.enabledHardware / GET /features. */
  hardwareId: HardwareId;
  image: string;
  nameKey: string;
  purposeKey: string;
  forKey: string;
  altKey: string;
  noteKey?: string;
}

const EQUIPMENT: readonly EquipmentCard[] = [
  {
    id: "temperatura",
    hardwareId: "milesight-em300-th",
    image: "/marketing/device-temp-humidity.jpg",
    nameKey: "equipTempName",
    purposeKey: "equipTempPurpose",
    forKey: "equipTempFor",
    altKey: "equipTempAlt",
  },
  {
    id: "sonda",
    hardwareId: "milesight-em500-pt100-t200",
    image: "/marketing/device-pt100.jpg",
    nameKey: "equipProbeName",
    purposeKey: "equipProbePurpose",
    forKey: "equipProbeFor",
    altKey: "equipProbeAlt",
    noteKey: "equipProbeNote",
  },
  {
    id: "tanques",
    hardwareId: "milesight-em410-rdl",
    image: "/marketing/device-level.jpg",
    nameKey: "equipLevelName",
    purposeKey: "equipLevelPurpose",
    forKey: "equipLevelFor",
    altKey: "equipLevelAlt",
  },
  {
    id: "fugas",
    hardwareId: "milesight-em300-sld",
    image: "/marketing/device-leak.jpg",
    nameKey: "equipLeakName",
    purposeKey: "equipLeakPurpose",
    forKey: "equipLeakFor",
    altKey: "equipLeakAlt",
    noteKey: "equipLeakNote",
  },
  {
    id: "puertas",
    hardwareId: "milesight-em300-mcs",
    image: "/marketing/device-door.jpg",
    nameKey: "equipDoorName",
    purposeKey: "equipDoorPurpose",
    forKey: "equipDoorFor",
    altKey: "equipDoorAlt",
    noteKey: "equipDoorNote",
  },
  {
    id: "presencia",
    hardwareId: "milesight-vs370",
    image: "/marketing/device-presence.jpg",
    nameKey: "equipPresenceName",
    purposeKey: "equipPresencePurpose",
    forKey: "equipPresenceFor",
    altKey: "equipPresenceAlt",
    noteKey: "equipPresenceNote",
  },
  {
    id: "corriente",
    hardwareId: "milesight-ct103",
    image: "/marketing/device-current.jpg",
    nameKey: "equipCurrentName",
    purposeKey: "equipCurrentPurpose",
    forKey: "equipCurrentFor",
    altKey: "equipCurrentAlt",
    noteKey: "equipCurrentNote",
  },
  {
    id: "controlador",
    hardwareId: "milesight-uc300",
    image: "/marketing/device-controller.jpg",
    nameKey: "equipControllerName",
    purposeKey: "equipControllerPurpose",
    forKey: "equipControllerFor",
    altKey: "equipControllerAlt",
    noteKey: "equipControllerNote",
  },
];

/**
 * Public page that explains, in plain language, what each unit ZonaClara
 * installs is for. Listing codes stay in the admin catalogue.
 * Gateways stay off this surface: end visitors care about what is watched,
 * not the radios that carry the readings (those remain in the admin catalogue).
 */
export function MarketingEquipmentPage() {
  const { t } = useTranslation("marketing");
  const features = useQuery(featuresQuery(apiClient));
  const enabledHardware = features.data?.enabledHardware;
  // Show the public cards at once. The controller stays out until the flags
  // say it is on, so a slow or failed /features does not blank the page and
  // does not flash a unit this deployment keeps off.
  const equipment = EQUIPMENT.filter((item) =>
    features.isSuccess
      ? isHardwareEnabledInMap(enabledHardware, item.hardwareId)
      : item.hardwareId !== "milesight-uc300",
  );

  useMarketingPageMeta("equipDocumentTitle", "equipMetaDescription", "/equipos");

  return (
    <MarketingChrome active="equipment">
      <main id="main">
        <section className="marketing-section-alt border-b border-border-subtle">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
            <p className="marketing-kicker">{t("brand")}</p>
            <h1 className="marketing-fade-up marketing-display mt-3 max-w-2xl text-3xl text-ink sm:text-4xl">
              {t("equipTitle")}
            </h1>
            <p className="marketing-fade-up marketing-delay-1 mt-4 max-w-2xl text-base text-ink-muted sm:text-lg">
              {t("equipLead")}
            </p>
          </div>
        </section>

        <section className="bg-surface">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
            <h2 className="marketing-display text-2xl text-ink sm:text-3xl">
              {t("equipSensorsTitle")}
            </h2>
            <p className="mt-3 max-w-2xl text-ink-muted">{t("equipSensorsLead")}</p>

            <ul className="mt-12 flex flex-col gap-16">
              {equipment.map((item, index) => (
                <EquipmentRow key={item.nameKey} item={item} index={index} />
              ))}
            </ul>

            <p className="mt-16 max-w-3xl border-l-2 border-brand pl-4 text-sm leading-relaxed text-ink-muted">
              {t("equipHonest")}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/#contact" className="marketing-cta marketing-cta-primary">
                {t("ctaContact")}
              </Link>
              <Link to="/" className="marketing-cta marketing-cta-secondary">
                {t("navHome")}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </MarketingChrome>
  );
}

function EquipmentRow({ item, index }: { item: EquipmentCard; index: number }) {
  const { t } = useTranslation("marketing");

  return (
    <li
      id={item.id}
      className={cn(
        "scroll-mt-24 grid items-center gap-8 lg:grid-cols-2 lg:gap-12",
        index % 2 === 1 && "lg:[&>div:first-child]:order-2",
      )}
    >
      <div>
        <img
          src={item.image}
          alt={t(item.altKey)}
          className="marketing-use-photo aspect-square w-full bg-surface-muted object-contain p-8"
          width={640}
          height={640}
          loading={index === 0 ? "eager" : "lazy"}
        />
      </div>
      <div>
        <hr className="marketing-rule" />
        <h3 className="marketing-display mt-4 text-2xl text-ink">{t(item.nameKey)}</h3>
        <p className="mt-3 text-ink-muted">{t(item.purposeKey)}</p>
        <p className="mt-5 text-sm">
          <span className="marketing-kicker">{t("equipForLabel")}</span>
          <span className="mt-1 block text-base text-ink">{t(item.forKey)}</span>
        </p>
        {item.noteKey ? <p className="mt-3 text-sm text-ink-muted">{t(item.noteKey)}</p> : null}
      </div>
    </li>
  );
}
